import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { useCommonContextProps } from '@/hooks';
import { isPharmaprixBanner, getBannerHostUrl } from '@/utils';
import { useMainContentWithGrid } from '@/utils/data-manipulation-utils';
import { trackProductSearch, getProductSearchRedirectContext } from '@/utils/analytics/analytics-utils';
import { ViewResolverComponent } from '../view-resolver';
import HeliosViewResolverComponentSSR from '../view-resolver-ssr/helios-view-resolver-ssr';

export default function CommonComponent({
  viewDefinition,
  themeTokens,
  pageId,
  headerSection,
  enableSSR = false,
}: {
  viewDefinition: any;
  themeTokens: any;
  pageId: any;
  headerSection: object | null | undefined;
  enableSSR?: boolean;
}) {
  const {
    commonContextProps: {
      settings: { banner },
    },
    cartCount,
    locale,
    currentDevice,
    pcOptimumAccount,
  } = useCommonContextProps();
  const router = useRouter();
  const updatedView = useMainContentWithGrid(viewDefinition);
  const [isRedirecting, setIsRedirecting] = useState(false);

  const searchRedirectData = viewDefinition?.searchRedirectData || {};
  
  // Handle redirect only on client side
  useEffect(() => {
    if (typeof window === 'undefined' || !searchRedirectData.isRedirectEnabled || isRedirecting) {
      return;
    }

    const hostNameUrl = getBannerHostUrl();
    const { phxRedirectUrl, sdmRedirectUrl } = searchRedirectData;
    const redirectUrl = isPharmaprixBanner() ? phxRedirectUrl : sdmRedirectUrl;
    const searchTerm = searchRedirectData?.searchTerm;
    
    // Track search redirect
    const searchRedirectContext = getProductSearchRedirectContext(searchRedirectData, viewDefinition);
    const globalContextFields = {
      cartCount,
      locale,
      currentDevice,
      pageId,
      pcOptimumAccount,
    };
    trackProductSearch(searchRedirectContext, globalContextFields);

    let targetUrl: URL;
    if (redirectUrl.startsWith('http://') || redirectUrl.startsWith('https://')) {
      targetUrl = new URL(redirectUrl);
      if (searchTerm) {
        targetUrl.searchParams.append('originalTarget', searchTerm);
      }
    } else {
      const formattedRedirectUrl =
        redirectUrl.substring(0, redirectUrl.indexOf('/c/')).toLowerCase() +
        redirectUrl.substring(redirectUrl.indexOf('/c/'));
      targetUrl = new URL(`${hostNameUrl}${formattedRedirectUrl}`);
      if (searchTerm) {
        targetUrl.searchParams.append('originalTarget', searchTerm);
      }
    }

    setIsRedirecting(true);
    router.push(targetUrl);
  }, [searchRedirectData, router, cartCount, locale, currentDevice, pageId, pcOptimumAccount, isRedirecting]);

  if (searchRedirectData.isRedirectEnabled && isRedirecting) {
    return null;
  }

  return (
    <div data-testid="main-page">
      {enableSSR ? (
        <HeliosViewResolverComponentSSR
          // @ts-ignore
          headerSection={headerSection}
          pageId={pageId}
          initialData={updatedView as any}
          themeTokens={themeTokens}
          enableSSR={enableSSR}
          seo={{
            banner,
            appTitle: 'Loblaw Digital',
            currentUrlBase: getBannerHostUrl(),
          }}
        />
      ) : (
        <ViewResolverComponent
          headerSection={headerSection}
          pageId={pageId}
          initialData={updatedView as any}
          themeTokens={themeTokens}
          enableSSR={enableSSR}
          seo={{
            banner,
            appTitle: 'Loblaw Digital',
            currentUrlBase: getBannerHostUrl(),
          }}
        />
      )}
    </div>
  );
}
